import React from "react";
import PropTypes from "prop-types";
import Radium from "radium";
import { DIAL_TYPES, dials } from "./constants";
import { getStyles } from "./getStyles";
import lightenDarkenColor from "../../utils/lightenDarkenColor";
import "./doubleDials.scss";

class DoubleDials extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      dial1FirstAnimation: "",
      dial1SecondAnimation: "",
      dial1AnimationEnd: "",
      dial2FirstAnimation: "",
      dial2SecondAnimation: "",
      dial2AnimationEnd: "",
      showCurrent: false,
      showPacing: false
    };

    this.colors = {
      dial1BgColor: lightenDarkenColor(this.props.dial1Color, -0.84),
      dial2BgColor: lightenDarkenColor(this.props.dial2Color, -0.84),
      dial1ShowColor: lightenDarkenColor(this.props.dial1Color, -0.3),
      dial2ShowColor: lightenDarkenColor(this.props.dial2Color, -0.3)
    };
  }

  componentDidMount() {
    if (dials.length) {
      dials.forEach((dial) => {
        this.renderDial(this.props, dial.type, dial.number);
        this.renderDial(this.props, dial.type, dial.number, "SecondAnimation");
        this.renderDial(this.props, dial.type, dial.number, "AnimationEnd");
      });
    }
  }

  renderDial(props, type, number, animType = "FirstAnimation") {
    const value = type === DIAL_TYPES.CURRENT ? props.currentData[0].value : props.pacingData[0].value;
    const percent = Math.max(value, 0) / 100;
    const angle = Math.round(percent * 360);
    const styleSheet = document.styleSheets[0];
    let keyframes;

    if (angle - 180 > 180 && animType !== "AnimationEnd") {
      keyframes = `@keyframes dial${number}${animType}-${angle} {
      0%,50% {transform: rotateZ(180deg);}
      100% {transform: rotateZ(180deg);}
    }`;
    } else if (angle > 180) {
      keyframes = `@keyframes dial${number}${animType}-${angle} {
        0% {transform: none;}
        50% {transform: rotateZ(180deg);}
        100% {transform: rotateZ(${animType === "FirstAnimation" ? "180" : angle}deg);}
      }`;

      if (animType === "SecondAnimation") {
        keyframes = `@keyframes dial${number}SecondAnimation-${angle} {
                         0%,50% {transform: rotateZ(0deg);}
                         100% {transform: rotateZ(${angle - 180}deg);}
                     }`;
      }
    } else {
      keyframes = `@keyframes dial${number}${animType}-${angle} {
        0% {transform: none;}
        100% {transform: rotateZ(${angle}deg);}
      }`;

      if (animType === "SecondAnimation") {
        keyframes = `@keyframes dial${number}SecondAnimation-${angle} {
        0%,50% {transform: rotateZ(0deg);}
        100% {transform: rotateZ(0deg);}
      }`;
      }
    }

    styleSheet.insertRule(keyframes, styleSheet.cssRules.length);
    this.setState({
      [`dial${number}${animType}`]: `dial${number}${animType}-${angle}`
    });
  }

  handleMouseChange = (type, shouldShow) => () => {
    const keyToUpdate = type === DIAL_TYPES.CURRENT ? "showCurrent" : "showPacing";

    this.setState({
      [keyToUpdate]: shouldShow
    });
  };

  render() {
    const { dial1Color, dial2Color } = this.props;
    const { dial1BgColor, dial2BgColor, dial1ShowColor, dial2ShowColor } = this.colors;
    const styles = getStyles(this.state);

    return (
      <div style={{ fontSize: "16px" }}>
        <div className="screen">
          <div
            className="percent-display"
            style={{
              color: dial1Color,
              opacity: this.state.showCurrent ? 1 : 0
            }}
          >
            {Math.round(this.props.currentData[0].value)}%
          </div>
          <div
            className="percent-display"
            style={{
              color: dial2Color,
              opacity: this.state.showPacing ? 1 : 0
            }}
          >
            {Math.round(this.props.pacingData[0].value)}%
          </div>
          <div className="dials">
            <div className="dial move">
              <div
                className="dial-background"
                style={{
                  backgroundColor: dial1BgColor
                }}
              />
              <div className="dial-container container1">
                <div
                  className="wedge"
                  style={{
                    ...styles.dial1FirstAnimation,
                    background: this.state.showCurrent ? dial1ShowColor : dial1Color
                  }}
                  onMouseEnter={this.handleMouseChange(DIAL_TYPES.CURRENT, true)}
                  onMouseLeave={this.handleMouseChange(DIAL_TYPES.CURRENT, false)}
                />
              </div>
              <div className="dial-container container2">
                <div
                  className="wedge"
                  style={{
                    ...styles.dial1SecondAnimation,
                    background: this.state.showCurrent ? dial1ShowColor : dial1Color
                  }}
                  onMouseEnter={this.handleMouseChange(DIAL_TYPES.CURRENT, true)}
                  onMouseLeave={this.handleMouseChange(DIAL_TYPES.CURRENT, false)}
                />
              </div>
              <div
                className="marker end"
                style={{
                  ...styles.dial1AnimationEnd,
                  background: this.state.showCurrent ? dial1ShowColor : dial1Color
                }}
                onMouseEnter={this.handleMouseChange(DIAL_TYPES.CURRENT, true)}
                onMouseLeave={this.handleMouseChange(DIAL_TYPES.CURRENT, false)}
              />
            </div>
            <div className="dial exercise">
              <div
                className="dial-background"
                style={{
                  backgroundColor: dial2BgColor
                }}
              />
              <div className="dial-container container1">
                <div
                  className="wedge"
                  style={{
                    ...styles.dial2FirstAnimation,
                    background: this.state.showPacing ? dial2ShowColor : dial2Color
                  }}
                  onMouseEnter={this.handleMouseChange(DIAL_TYPES.PACING, true)}
                  onMouseLeave={this.handleMouseChange(DIAL_TYPES.PACING, false)}
                />
              </div>
              <div className="dial-container container2">
                <div
                  className="wedge"
                  style={{
                    ...styles.dial2SecondAnimation,
                    background: this.state.showPacing ? dial2ShowColor : dial2Color
                  }}
                  onMouseEnter={this.handleMouseChange(DIAL_TYPES.PACING, true)}
                  onMouseLeave={this.handleMouseChange(DIAL_TYPES.PACING, false)}
                />
              </div>
              <div
                className="marker end"
                style={{
                  ...styles.dial2AnimationEnd,
                  background: this.state.showPacing ? dial2ShowColor : dial2Color
                }}
                onMouseEnter={this.handleMouseChange(DIAL_TYPES.PACING, true)}
                onMouseLeave={this.handleMouseChange(DIAL_TYPES.PACING, false)}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

DoubleDials.propTypes = {
  currentData: PropTypes.array,
  pacingData: PropTypes.array,
  dial1Color: PropTypes.string,
  dial2Color: PropTypes.string
};

DoubleDials.defaultProps = {
  dial1Color: "#4a9bff",
  dial2Color: "#fc6a3e"
};

export default Radium(DoubleDials);
